import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText, Mail, Radio } from 'lucide-react';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';

interface SefazLog {
  id: number;
  emitente_id: number | null;
  operacao: string;
  ambiente: string | null;
  url_destino: string | null;
  http_status: number | null;
  c_stat_sefaz: string | null;
  duracao_ms: number | null;
  erro_mensagem: string | null;
  created_at: string | null;
}

interface EmailLog {
  id: number;
  tipo_documento: string | null;
  documento_id: number | null;
  destinatarios: string;
  assunto: string;
  status: string;
  erro_mensagem: string | null;
  smtp_response: string | null;
  created_at: string | null;
  enviado_em: string | null;
}

function fmt(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR');
}

export default function AuditoriaPage() {
  const [aba, setAba] = useState<'sefaz' | 'emails'>('sefaz');

  const sefazQ = useQuery({
    queryKey: ['auditoria-sefaz'],
    queryFn: async () => (await api.get<SefazLog[]>('/auditoria/sefaz')).data,
    enabled: aba === 'sefaz',
  });

  const emailsQ = useQuery({
    queryKey: ['auditoria-emails'],
    queryFn: async () => (await api.get<EmailLog[]>('/auditoria/emails')).data,
    enabled: aba === 'emails',
  });

  return (
    <div className="space-y-4">
      <div>
        <div className="eyebrow">Sistema</div>
        <h1 className="h-display text-xl text-slate-950 mt-0.5 flex items-center gap-2 dark:text-white">
          <ScrollText className="h-5 w-5 text-sky-700 dark:text-sky-400" /> Auditoria
        </h1>
      </div>

      <div className="card-surface p-2 inline-flex gap-1 rounded-lg">
        <button
          type="button"
          onClick={() => setAba('sefaz')}
          className={`px-3 py-1.5 text-xs rounded-md font-semibold transition ${
            aba === 'sefaz' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' : 'text-neutral-500 hover:text-slate-950 dark:text-neutral-400 dark:hover:text-white'
          }`}
        >
          <Radio className="h-3.5 w-3.5 inline mr-1" /> SEFAZ ({sefazQ.data?.length ?? 0})
        </button>
        <button
          type="button"
          onClick={() => setAba('emails')}
          className={`px-3 py-1.5 text-xs rounded-md font-semibold transition ${
            aba === 'emails' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' : 'text-neutral-500 hover:text-slate-950 dark:text-neutral-400 dark:hover:text-white'
          }`}
        >
          <Mail className="h-3.5 w-3.5 inline mr-1" /> E-mails ({emailsQ.data?.length ?? 0})
        </button>
      </div>

      {aba === 'sefaz' && (
        <div className="card-surface overflow-hidden">
          {sefazQ.isLoading && <div className="py-10 grid place-items-center"><LoadingSpinner /></div>}
          {!sefazQ.isLoading && (sefazQ.data?.length ?? 0) === 0 && (
            <div className="py-10 text-center text-sm text-neutral-600 dark:text-neutral-400">
              Nenhuma requisição SEFAZ registrada ainda.
            </div>
          )}
          {!sefazQ.isLoading && (sefazQ.data?.length ?? 0) > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-slate-200 dark:border-white/10">
                  <tr>
                    <th className="py-2 px-3">Quando</th>
                    <th className="py-2 px-3">Operação</th>
                    <th className="py-2 px-3">Emit.</th>
                    <th className="py-2 px-3">Amb.</th>
                    <th className="py-2 px-3">HTTP</th>
                    <th className="py-2 px-3">cStat</th>
                    <th className="py-2 px-3">Duração</th>
                    <th className="py-2 px-3">Erro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {sefazQ.data!.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                      <td className="py-2 px-3 text-xs text-neutral-600 dark:text-neutral-400 whitespace-nowrap">{fmt(r.created_at)}</td>
                      <td className="py-2 px-3 mono">{r.operacao}</td>
                      <td className="py-2 px-3 text-xs">{r.emitente_id ?? '-'}</td>
                      <td className="py-2 px-3 text-xs">{r.ambiente ?? '-'}</td>
                      <td className="py-2 px-3 mono text-xs">{r.http_status ?? '-'}</td>
                      <td className="py-2 px-3 mono text-xs">{r.c_stat_sefaz ?? '-'}</td>
                      <td className="py-2 px-3 mono text-xs tab-nums">{r.duracao_ms ? `${r.duracao_ms}ms` : '-'}</td>
                      <td className="py-2 px-3 text-xs text-rose-700 max-w-md truncate dark:text-rose-300" title={r.erro_mensagem || ''}>
                        {r.erro_mensagem || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {aba === 'emails' && (
        <div className="card-surface overflow-hidden">
          {emailsQ.isLoading && <div className="py-10 grid place-items-center"><LoadingSpinner /></div>}
          {!emailsQ.isLoading && (emailsQ.data?.length ?? 0) === 0 && (
            <div className="py-10 text-center text-sm text-neutral-600 dark:text-neutral-400">
              Nenhum e-mail registrado.
            </div>
          )}
          {!emailsQ.isLoading && (emailsQ.data?.length ?? 0) > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-slate-200 dark:border-white/10">
                  <tr>
                    <th className="py-2 px-3">Quando</th>
                    <th className="py-2 px-3">Tipo</th>
                    <th className="py-2 px-3">Doc</th>
                    <th className="py-2 px-3">Destinatários</th>
                    <th className="py-2 px-3">Assunto</th>
                    <th className="py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {emailsQ.data!.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                      <td className="py-2 px-3 text-xs text-neutral-600 dark:text-neutral-400 whitespace-nowrap">{fmt(r.enviado_em || r.created_at)}</td>
                      <td className="py-2 px-3 text-xs uppercase">{r.tipo_documento ?? '-'}</td>
                      <td className="py-2 px-3 mono text-xs">#{r.documento_id ?? '-'}</td>
                      <td className="py-2 px-3 text-xs max-w-xs truncate" title={r.destinatarios}>{r.destinatarios}</td>
                      <td className="py-2 px-3 text-xs max-w-md truncate" title={r.assunto}>{r.assunto}</td>
                      <td className="py-2 px-3"><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
